import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Checkbox,
    Chip,
    Stack,
    Typography,
    Box,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { User, statusColors } from '../types/UserTypes';

interface UserTableProps {
    users: User[];
    selectedUsers: number[];
    onSelectUser: (userId: number, selected: boolean) => void;
    onSelectAll: (selected: boolean) => void;
}

const UserTable: React.FC<UserTableProps> = ({
    users,
    selectedUsers,
    onSelectUser,
    onSelectAll,
}) => {
    const navigate = useNavigate();

    const handleSelectUser = (userId: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
        onSelectUser(userId, event.target.checked);
    };

    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        onSelectAll(event.target.checked);
    };

    const handleEdit = (user: User) => () => {
        navigate(`/edit-user/${user.apiId}`);
    };

    const isAllSelected = users.length > 0 && selectedUsers.length === users.length;
    const isIndeterminate = selectedUsers.length > 0 && selectedUsers.length < users.length;

    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell padding='checkbox'>
                        <Checkbox
                            checked={isAllSelected}
                            indeterminate={isIndeterminate}
                            onChange={handleSelectAll}
                        />
                    </TableCell>
                    <TableCell sx={{ fontWeight: '600', fontSize: '12px' }}>
                        Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: '600', fontSize: '12px' }}>
                        Username
                    </TableCell>
                    <TableCell sx={{ fontWeight: '600', fontSize: '12px' }}>
                        Roles
                    </TableCell>
                    <TableCell sx={{ fontWeight: '600', fontSize: '12px' }}>
                        Last Changed
                    </TableCell>
                    <TableCell sx={{ fontWeight: '600', fontSize: '12px' }}>
                        Last Access
                    </TableCell>
                    <TableCell />
                </TableRow>
            </TableHead>
            <TableBody>
                {users.map((user) => (
                    <TableRow
                        key={user.id}
                        sx={{
                            backgroundColor: '#FAF9F9',
                            borderRadius: '12px',
                            boxShadow: '0px 0px 1px rgba(0,0,0,0.08)',
                            '& td': { borderBottom: 'none' },
                        }}
                    >
                        <TableCell padding='checkbox'>
                            <Checkbox
                                checked={selectedUsers.includes(user.id)}
                                onChange={handleSelectUser(user.id)}
                            />
                        </TableCell>
                        <TableCell>
                            <Chip
                                label={
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 7,
                                                height: 7,
                                                borderRadius: '50%',
                                                backgroundColor: statusColors[user.status].color,
                                            }}
                                        />
                                        <span>{user.status}</span>
                                    </Box>
                                }
                                size='small'
                                sx={{
                                    color: statusColors[user.status].color,
                                    backgroundColor: statusColors[user.status].bg,
                                    fontFamily: 'Poppins, sans-serif',
                                    fontSize: 12,
                                    borderRadius: '15px',
                                    px: 1.5,
                                    fontWeight: 600,
                                    height: 30,
                                }}
                            />
                        </TableCell>

                        <TableCell>
                            <Typography
                                sx={{
                                    fontFamily: 'Poppins, sans-serif',
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: '#2979F1',
                                    textDecoration: 'underline',
                                }}
                            >
                                {user.username}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Stack spacing={0.5}>
                                {user.roles.map((role, i) => (
                                    <Chip
                                        key={i}
                                        label={role}
                                        size='small'
                                        sx={{
                                            fontFamily: 'Poppins, sans-serif',
                                            fontSize: 12,
                                            backgroundColor: '#ECECEC',
                                            color: '#203A6B',
                                            borderRadius: '13px',
                                            fontWeight: '600',
                                            width: '150px',
                                        }}
                                    />
                                ))}
                            </Stack>
                        </TableCell>
                        <TableCell>
                            <Typography
                                fontSize={12}
                                fontWeight={400}
                                fontFamily='Poppins, sans-serif'
                            >
                                {user.changed}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography
                                fontSize={12}
                                fontWeight={400}
                                fontFamily='Poppins, sans-serif'
                            >
                                {user.access}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography
                                onClick={handleEdit(user)}
                                sx={{
                                    fontFamily: 'Poppins, sans-serif',
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: '#2979F1',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                }}
                            >
                                Edit
                            </Typography>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};

export default UserTable; 